const findInRepeatableRow = (currentForm, repeatableIndex, predicate) => {
  for (const field of currentForm.fields) {
    if (Array.isArray(field.fields?.[repeatableIndex])) {
      const found = field.fields[repeatableIndex].find(predicate);
      if (found !== undefined) return found;
    }
  }
  return undefined;
};

export const getCascadingFields = ({ forms, formIndex, repeatableIndex = null }) => {
  const currentForm = forms[formIndex];

  if (repeatableIndex !== null) {
    for (const field of currentForm.fields) {
      if (Array.isArray(field.fields?.[repeatableIndex])) {
        return field.fields[repeatableIndex].filter(f => f.data_source === "from_cascading_set");
      }
    }
  }

  return currentForm.fields.filter(field => field.data_source === "from_cascading_set");
};

export const getFieldValue = ({ forms, formIndex, fieldCode, repeatableIndex = null }) => {
  const currentForm = forms[formIndex];

  if (repeatableIndex !== null) {
    const found = findInRepeatableRow(currentForm, repeatableIndex, f => f.field_code === fieldCode);
    if (found !== undefined) return found.value;
  }

  return currentForm.fields.find(f => f.field_code === fieldCode)?.value;
};

export const findFieldIndices = ({ forms, formIndex, fieldCode, repeatableIndex = null }) => {
  const currentForm = forms[formIndex];

  if (repeatableIndex !== null) {
    for (let i = 0; i < currentForm.fields.length; i++) {
      const field = currentForm.fields[i];
      if (Array.isArray(field.fields?.[repeatableIndex])) {
        const repeatableFieldIndex = field.fields[repeatableIndex].findIndex(f => f.field_code === fieldCode);
        if (repeatableFieldIndex !== -1) {
          return { fieldIndex: i, repeatableFieldIndex };
        }
      }
    }
  }

  const fieldIndex = currentForm.fields.findIndex(f => f.field_code === fieldCode);
  return fieldIndex !== -1 ? { fieldIndex, repeatableFieldIndex: null } : null;
};

export const isCascadingFieldEnabled = ({ forms, data, formIndex, repeatableIndex = null }) => {
  if (data.data_source !== "from_cascading_set") return true;
  if (!data.cascading_main_field_code) return true;

  const parentValue = getFieldValue({ forms, formIndex, fieldCode: data.cascading_main_field_code, repeatableIndex });
  return parentValue && (typeof parentValue === "object" ? parentValue.value : parentValue);
};

export const getCascadingOptions = ({ forms, data, formIndex, repeatableIndex = null }) => {
  if (data.data_source !== "from_cascading_set") {
    return data.values.map(value => ({ value, label: value }));
  }

  const options = [];

  if (!data.cascading_main_field_code) {
    data?.cascading_values?.forEach((value) => {
      Object.entries(value).forEach(([key, val]) => {
        options.push({ value: key, label: val });
      });
    });
  } else {
    const parentValue = getFieldValue({ forms, formIndex, fieldCode: data.cascading_main_field_code, repeatableIndex });
    const parentKey = typeof parentValue === "object" ? parentValue.value : parentValue;

    if (parentKey) {
      data?.cascading_values?.forEach((cascadingGroup) => {
        if (cascadingGroup[parentKey]) {
          const subOptions = cascadingGroup[parentKey];
          if (Array.isArray(subOptions)) {
            subOptions.forEach((subOption) => {
              Object.entries(subOption).forEach(([key, val]) => {
                options.push({ value: key, label: val });
              });
            });
          }
        }
      });
    }
  }

  return options;
};

export const getCascadingLabelById = (field, valueId) => {
  if (!field.cascading_values || !valueId) return valueId;

  for (const cascadingGroup of field.cascading_values) {
    for (const [key, value] of Object.entries(cascadingGroup)) {
      if (key === valueId) return value;

      if (Array.isArray(value)) {
        for (const subOption of value) {
          for (const [subKey, subValue] of Object.entries(subOption)) {
            if (subKey === valueId) return subValue;
          }
        }
      }
    }
  }

  return valueId;
};

export const getDynamicPlaceholder = ({ forms, data, formIndex, repeatableIndex = null }) => {
  if (data.data_source !== "from_cascading_set" || !data.cascading_main_field_code) {
    return null;
  }

  const currentForm = forms[formIndex];

  let parentField = null;
  if (repeatableIndex !== null) {
    parentField = findInRepeatableRow(currentForm, repeatableIndex, f => f.field_code === data.cascading_main_field_code) ?? null;
  }
  if (!parentField) {
    parentField = currentForm.fields.find(f => f.field_code === data.cascading_main_field_code) ?? null;
  }

  const parentValue = getFieldValue({ forms, formIndex, fieldCode: data.cascading_main_field_code, repeatableIndex });
  const hasParentValue = parentValue && (typeof parentValue === "object" ? parentValue.value : parentValue);

  if (!hasParentValue && parentField) {
    return {
      key: "forms.place-holders.select-first-the-level",
      level: parentField.field_label,
    };
  }

  return null;
};
